import { History } from "lucide-react";
import { fmtUsd, fmtPrice } from "@/lib/api";
import { cn } from "@/lib/utils";

export const TradeHistory = ({ trades }) => {
  return (
    <div data-testid="trade-history" className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)]">
      <div className="flex items-center gap-2 px-5 h-14 border-b border-[var(--border)]">
        <History className="h-4 w-4 text-[var(--text-faint)]" />
        <h3 className="font-head font-bold text-base">İşlem Geçmişi</h3>
      </div>
      <div className="hidden md:block overflow-x-auto max-h-[360px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[var(--app-bg)]">
            <tr className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] border-b border-[var(--surface-hover)]">
              <th className="text-left font-normal py-2.5 px-5">Tür</th>
              <th className="text-left font-normal py-2.5 px-3">Varlık</th>
              <th className="text-left font-normal py-2.5 px-3">Neden</th>
              <th className="text-right font-normal py-2.5 px-3">Fiyat</th>
              <th className="text-right font-normal py-2.5 px-3">Tutar</th>
              <th className="text-right font-normal py-2.5 px-5">Zaman</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {trades.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-[var(--text-faint)] text-sm font-sans">Henüz işlem yok</td></tr>
            )}
            {trades.map((t) => {
              const buy = t.action === "BUY";
              const reasonMap = { take_profit: "Kâr Al", stop_loss: "Stop-Loss", signal_close: "Haber / Risk Çıkışı", signal: "Manuel", open: "Alım", dca_buy: "Kademeli Alım", grid_buy: "Grid Alım", grid_sell: "Grid Satış", bot_stop: "Bot Durdurma" };
              const reasonColor = { take_profit: "text-[var(--pos)]", stop_loss: "text-[var(--neg)]", signal_close: "text-[var(--accent)]", signal: "text-[var(--text-dim)]", open: "text-[var(--text-faint)]" };
              return (
                <tr key={t.id || `${t.symbol}-${t.timestamp}`} data-testid={`trade-row-${t.id}`} className="border-b border-[var(--surface-hover)] hover:bg-[var(--surface-hover)] transition-colors">
                  <td className="py-2.5 px-5">
                    <span className={cn("text-xs font-bold px-1.5 py-0.5 rounded",
                      buy ? "bg-[#00FFA3]/10 text-[var(--pos)]" : "bg-[#FF3366]/10 text-[var(--neg)]")}>
                      {buy ? "ALIM" : "SATIM"}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 font-head font-bold">{t.symbol}</td>
                  <td className={cn("py-2.5 px-3 text-xs", reasonColor[t.reason] || "text-[var(--text-faint)]")}>
                    {reasonMap[t.reason] || "--"}
                    {t.action === "SELL" && t.fee_complete === false && <span data-testid={`trade-fee-pending-${t.id}`} className="block text-[var(--warn)]">Ücret bekleniyor</span>}
                  </td>
                  <td data-testid={`trade-price-${t.id}`} className="py-2.5 px-3 text-right text-[var(--text-dim)]">{fmtPrice(t.price)}</td>
                  <td className="py-2.5 px-3 text-right">{fmtUsd(t.usdt)}</td>
                  <td className="py-2.5 px-5 text-right text-[var(--text-faint)] text-xs">
                    {new Date(t.timestamp).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-[var(--border)] max-h-[420px] overflow-y-auto">
        {trades.length === 0 && (
          <p className="text-center py-8 text-[var(--text-faint)] text-sm">Henüz işlem yok</p>
        )}
        {trades.map((t) => {
          const buy = t.action === "BUY";
          const reasonMap = { take_profit: "Kâr Al", stop_loss: "Stop-Loss", signal_close: "Haber / Risk Çıkışı", signal: "Manuel", open: "Alım", dca_buy: "Kademeli Alım", grid_buy: "Grid Alım", grid_sell: "Grid Satış", bot_stop: "Bot Durdurma" };
          const reasonColor = { take_profit: "text-[var(--pos)]", stop_loss: "text-[var(--neg)]", signal_close: "text-[var(--accent)]", signal: "text-[var(--text-dim)]", open: "text-[var(--text-faint)]" };
          return (
            <div key={t.id || `${t.symbol}-${t.timestamp}`} data-testid={`trade-card-${t.id}`} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded",
                    buy ? "bg-[#00FFA3]/10 text-[var(--pos)]" : "bg-[#FF3366]/10 text-[var(--neg)]")}>
                    {buy ? "ALIM" : "SATIM"}
                  </span>
                  <span className="font-head font-bold text-sm">{t.symbol}</span>
                </div>
                <span className="text-[11px] text-[var(--text-faint)] font-mono">
                  {new Date(t.timestamp).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs font-mono">
                <div className="flex justify-between gap-2 col-span-2"><span className="text-[var(--text-faint)]">Fiyat</span><span data-testid={`trade-price-mobile-${t.id}`} className="text-[var(--text-dim)] break-all text-right">{fmtPrice(t.price)}</span></div>
                {t.action === "SELL" && t.fee_complete === false && <p data-testid={`trade-fee-pending-mobile-${t.id}`} className="col-span-2 text-[var(--warn)]">Ücret bekleniyor</p>}
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Tutar</span><span>{fmtUsd(t.usdt)}</span></div>
                <div className="flex justify-between col-span-2"><span className="text-[var(--text-faint)]">Neden</span>
                  <span className={reasonColor[t.reason] || "text-[var(--text-faint)]"}>{reasonMap[t.reason] || "--"}</span></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
